import { Task } from '../types/task';
import { Project } from '../types/project';
import { User } from '../types/user';
import { TimeEntry } from '../types/time-entry';
import { WeeklyPlan } from '../types/weekly-plan';

export interface ProjectMetrics {
    totalProjects: number;
    projectsWithTasks: number;
    projectsWithoutTasks: number;
    tasksByProject: { [projectId: string]: number };
}

export interface TaskMetrics {
    totalTasks: number;
    completedTasks: number;
    inProgressTasks: number;
    todoTasks: number;
    overdueTasks: number;
    unassignedTasks: number;
    tasksByStatus: { [status: string]: number };
    tasksByPriority: { [priority: string]: number };
}

export interface ResourceMetrics {
    totalUsers: number;
    activeUsers: number;
    tasksByUser: { [userId: string]: number };
    overdueTasksByUser: { [userId: string]: number };
    userWorkload: { userId: string; userName: string; taskCount: number; overdueCount: number }[];
}

export interface GanttTask {
    id: string;
    title: string;
    projectId: string;
    projectName: string;
    assignedTo?: string;
    assignedToName?: string;
    status: string;
    priority: string;
    dueDate?: string;
    createdAt: string;
}

export const calculateProjectMetrics = (projects: Project[], tasks: Task[]): ProjectMetrics => {
    const tasksByProject: { [projectId: string]: number } = {};

    tasks.forEach(task => {
        tasksByProject[task.project_id] = (tasksByProject[task.project_id] || 0) + 1;
    });

    const projectsWithTasks = Object.keys(tasksByProject).length;

    return {
        totalProjects: projects.length,
        projectsWithTasks,
        projectsWithoutTasks: projects.length - projectsWithTasks,
        tasksByProject,
    };
};

export const calculateTaskMetrics = (tasks: Task[]): TaskMetrics => {
    const now = new Date();
    const tasksByStatus: { [status: string]: number } = {};
    const tasksByPriority: { [priority: string]: number } = {};

    let completedTasks = 0;
    let inProgressTasks = 0;
    let todoTasks = 0;
    let overdueTasks = 0;
    let unassignedTasks = 0;

    tasks.forEach(task => {
        // Count by status
        tasksByStatus[task.status] = (tasksByStatus[task.status] || 0) + 1;

        // Count by priority
        tasksByPriority[task.priority] = (tasksByPriority[task.priority] || 0) + 1;

        // Specific status counts
        if (task.status === 'completed') completedTasks++;
        else if (task.status === 'in_progress') inProgressTasks++;
        else if (task.status === 'todo') todoTasks++;

        // Overdue check
        if (task.due_date && task.status !== 'completed') {
            const dueDate = new Date(task.due_date);
            if (dueDate < now) overdueTasks++;
        }

        // Unassigned check
        if (!task.assigned_to) unassignedTasks++;
    });

    return {
        totalTasks: tasks.length,
        completedTasks,
        inProgressTasks,
        todoTasks,
        overdueTasks,
        unassignedTasks,
        tasksByStatus,
        tasksByPriority,
    };
};

export const calculateResourceMetrics = (users: User[], tasks: Task[]): ResourceMetrics => {
    const now = new Date();
    const tasksByUser: { [userId: string]: number } = {};
    const overdueTasksByUser: { [userId: string]: number } = {};

    tasks.forEach(task => {
        if (task.assigned_to) {
            tasksByUser[task.assigned_to] = (tasksByUser[task.assigned_to] || 0) + 1;

            // Check if overdue
            if (task.due_date && task.status !== 'completed') {
                const dueDate = new Date(task.due_date);
                if (dueDate < now) {
                    overdueTasksByUser[task.assigned_to] = (overdueTasksByUser[task.assigned_to] || 0) + 1;
                }
            }
        }
    });

    const activeUsers = Object.keys(tasksByUser).length;

    const userWorkload = users.map(user => ({
        userId: user.id,
        userName: user.full_name || user.username,
        taskCount: tasksByUser[user.id] || 0,
        overdueCount: overdueTasksByUser[user.id] || 0,
    })).sort((a, b) => b.taskCount - a.taskCount);

    return {
        totalUsers: users.length,
        activeUsers,
        tasksByUser,
        overdueTasksByUser,
        userWorkload,
    };
};

export const prepareGanttData = (
    tasks: Task[],
    projects: Project[],
    users: User[]
): GanttTask[] => {
    const projectMap = new Map(projects.map(p => [p.id, p.name]));
    const userMap = new Map(users.map(u => [u.id, u.full_name || u.username]));

    return tasks
        .filter(task => task.due_date) // Only tasks with due dates
        .map(task => ({
            id: task.id,
            title: task.title,
            projectId: task.project_id,
            projectName: projectMap.get(task.project_id) || `Project #${task.project_id}`,
            assignedTo: task.assigned_to ?? undefined,
            assignedToName: task.assigned_to ? userMap.get(task.assigned_to) : undefined,
            status: task.status,
            priority: task.priority,
            dueDate: task.due_date ?? undefined,
            createdAt: task.created_at,
        }))
        .sort((a, b) => {
            // Sort by project first, then by due date
            if (a.projectId !== b.projectId) return a.projectId.localeCompare(b.projectId);
            return new Date(a.dueDate!).getTime() - new Date(b.dueDate!).getTime();
        });
};

export const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
        case 'completed':
            return '#4caf50';
        case 'in_progress':
            return '#2196f3';
        case 'todo':
            return '#ff9800';
        default:
            return '#9e9e9e';
    }
};

export const getPriorityColor = (priority: string): string => {
    switch (priority.toLowerCase()) {
        case 'high':
            return '#f44336';
        case 'medium':
            return '#ff9800';
        case 'low':
            return '#4caf50';
        default:
            return '#9e9e9e';
    }
};

// ===========================
// TIME TRACKING UTILITIES
// ===========================

export interface TimeTrackingMetrics {
    totalHours: number;
    billableHours: number;
    nonBillableHours: number;
    hoursByProject: { projectId: string; projectName: string; hours: number; billableHours: number }[];
    hoursByUser: { userId: string; userName: string; hours: number; billableHours: number }[];
    utilizationRate: number; // Percentage of hours logged vs available
}

export interface UserUtilization {
    userId: string;
    userName: string;
    totalHoursLogged: number;
    billableHours: number;
    nonBillableHours: number;
    utilizationRate: number; // Percentage of capacity used
    projects: { projectId: string; projectName: string; hours: number }[];
}

export interface PlanVsActualMetrics {
    weekStartDate: string;
    weekEndDate: string;
    totalPlannedHours: number;
    totalActualHours: number;
    variance: number;
    variancePercent: number;
    projectBreakdown: {
        projectId: string;
        projectName: string;
        plannedHours: number;
        actualHours: number;
        variance: number;
        variancePercent: number;
    }[];
    userBreakdown: {
        userId: string;
        userName: string;
        plannedHours: number;
        actualHours: number;
        variance: number;
        variancePercent: number;
    }[];
}

export const calculateTimeTrackingMetrics = (
    timeEntries: TimeEntry[],
    projects: Project[],
    users: User[]
): TimeTrackingMetrics => {
    const projectMap = new Map(projects.map(p => [p.id, p.name]));
    const userMap = new Map(users.map(u => [u.id, u.full_name || u.username]));

    let totalHours = 0;
    let billableHours = 0;
    let nonBillableHours = 0;

    const hoursByProjectMap = new Map<string, { hours: number; billableHours: number }>();
    const hoursByUserMap = new Map<string, { hours: number; billableHours: number }>();

    timeEntries.forEach(entry => {
        totalHours += entry.hours;

        if (entry.billable) {
            billableHours += entry.hours;
        } else {
            nonBillableHours += entry.hours;
        }

        // By project (only if project_id exists)
        if (entry.project_id) {
            const projectData = hoursByProjectMap.get(entry.project_id) || { hours: 0, billableHours: 0 };
            projectData.hours += entry.hours;
            if (entry.billable) projectData.billableHours += entry.hours;
            hoursByProjectMap.set(entry.project_id, projectData);
        }

        // By user
        const userData = hoursByUserMap.get(entry.user_id) || { hours: 0, billableHours: 0 };
        userData.hours += entry.hours;
        if (entry.billable) userData.billableHours += entry.hours;
        hoursByUserMap.set(entry.user_id, userData);
    });

    const hoursByProject = Array.from(hoursByProjectMap.entries())
        .map(([projectId, data]) => ({
            projectId,
            projectName: projectMap.get(projectId) || `Project #${projectId}`,
            hours: data.hours,
            billableHours: data.billableHours,
        }))
        .sort((a, b) => b.hours - a.hours);

    const hoursByUser = Array.from(hoursByUserMap.entries())
        .map(([userId, data]) => ({
            userId,
            userName: userMap.get(userId) || `User #${userId}`,
            hours: data.hours,
            billableHours: data.billableHours,
        }))
        .sort((a, b) => b.hours - a.hours);

    return {
        totalHours,
        billableHours,
        nonBillableHours,
        hoursByProject,
        hoursByUser,
        utilizationRate: 0, // Calculated separately based on weekly plans
    };
};

export const calculateUserUtilization = (
    timeEntries: TimeEntry[],
    weeklyPlans: WeeklyPlan[],
    projects: Project[],
    users: User[],
    weekStartDate: string,
    weekEndDate: string
): UserUtilization[] => {
    const projectMap = new Map(projects.map(p => [p.id, p.name]));
    const userMap = new Map(users.map(u => [u.id, u.full_name || u.username]));

    // Filter time entries for the specified week
    const weekEntries = timeEntries.filter(entry =>
        entry.date >= weekStartDate && entry.date <= weekEndDate
    );

    // Group by user
    const userDataMap = new Map<string, {
        totalHoursLogged: number;
        billableHours: number;
        nonBillableHours: number;
        projectHours: Map<string, number>;
        plannedHours: number;
    }>();

    weekEntries.forEach(entry => {
        const userData = userDataMap.get(entry.user_id) || {
            totalHoursLogged: 0,
            billableHours: 0,
            nonBillableHours: 0,
            projectHours: new Map(),
            plannedHours: 0,
        };

        userData.totalHoursLogged += entry.hours;
        if (entry.billable) {
            userData.billableHours += entry.hours;
        } else {
            userData.nonBillableHours += entry.hours;
        }

        if (entry.project_id) {
            const projectHours = userData.projectHours.get(entry.project_id) || 0;
            userData.projectHours.set(entry.project_id, projectHours + entry.hours);
        }

        userDataMap.set(entry.user_id, userData);
    });

    // Add planned hours from weekly plans
    weeklyPlans.forEach(plan => {
        if (plan.week_start_date === weekStartDate && plan.week_end_date === weekEndDate) {
            const userData = userDataMap.get(plan.user_id) || {
                totalHoursLogged: 0,
                billableHours: 0,
                nonBillableHours: 0,
                projectHours: new Map(),
                plannedHours: 0,
            };
            const productiveHours = (plan.allocations ?? []).reduce((sum, a) => sum + a.hours, 0);
            const nonProductiveHours = (plan.non_productive_allocations || []).reduce((sum, a) => sum + a.hours, 0);
            userData.plannedHours = productiveHours + nonProductiveHours;
            userDataMap.set(plan.user_id, userData);
        }
    });

    return Array.from(userDataMap.entries())
        .map(([userId, data]) => ({
            userId,
            userName: userMap.get(userId) || `User #${userId}`,
            totalHoursLogged: data.totalHoursLogged,
            billableHours: data.billableHours,
            nonBillableHours: data.nonBillableHours,
            utilizationRate: data.plannedHours > 0
                ? (data.totalHoursLogged / data.plannedHours) * 100
                : 0,
            projects: Array.from(data.projectHours.entries())
                .map(([projectId, hours]) => ({
                    projectId,
                    projectName: projectMap.get(projectId) || `Project #${projectId}`,
                    hours,
                }))
                .sort((a, b) => b.hours - a.hours),
        }))
        .sort((a, b) => b.totalHoursLogged - a.totalHoursLogged);
};

export const calculatePlanVsActuals = (
    timeEntries: TimeEntry[],
    weeklyPlans: WeeklyPlan[],
    projects: Project[],
    users: User[],
    weekStartDate: string,
    weekEndDate: string
): PlanVsActualMetrics | null => {
    const projectMap = new Map(projects.map(p => [p.id, p.name]));
    const userMap = new Map(users.map(u => [u.id, u.full_name || u.username]));

    // Filter time entries for the week
    const weekEntries = timeEntries.filter(entry =>
        entry.date >= weekStartDate && entry.date <= weekEndDate
    );

    // Filter plans for the week
    const weekPlans = weeklyPlans.filter(plan =>
        plan.week_start_date === weekStartDate && plan.week_end_date === weekEndDate
    );

    if (weekPlans.length === 0) {
        return null; // No plans for this week
    }

    // Calculate actual hours by project
    const actualByProject = new Map<string, number>();
    weekEntries.forEach(entry => {
        if (entry.project_id) {
            const current = actualByProject.get(entry.project_id) || 0;
            actualByProject.set(entry.project_id, current + entry.hours);
        }
    });

    // Calculate actual hours by user
    const actualByUser = new Map<string, number>();
    weekEntries.forEach(entry => {
        const current = actualByUser.get(entry.user_id) || 0;
        actualByUser.set(entry.user_id, current + entry.hours);
    });

    // Calculate planned hours by project
    const plannedByProject = new Map<string, number>();
    weekPlans.forEach(plan => {
        (plan.allocations ?? []).forEach(allocation => {
            const current = plannedByProject.get(allocation.project_id) || 0;
            plannedByProject.set(allocation.project_id, current + allocation.hours);
        });
    });

    // Calculate planned hours by user
    const plannedByUser = new Map<string, number>();
    weekPlans.forEach(plan => {
        const productiveHours = (plan.allocations ?? []).reduce((sum, a) => sum + a.hours, 0);
        const nonProductiveHours = (plan.non_productive_allocations || []).reduce((sum, a) => sum + a.hours, 0);
        plannedByUser.set(plan.user_id, productiveHours + nonProductiveHours);
    });

    // Total calculations
    const totalPlannedHours = Array.from(plannedByUser.values()).reduce((sum, h) => sum + h, 0);
    const totalActualHours = weekEntries.reduce((sum, entry) => sum + entry.hours, 0);
    const variance = totalActualHours - totalPlannedHours;
    const variancePercent = totalPlannedHours > 0 ? (variance / totalPlannedHours) * 100 : 0;

    // Project breakdown
    const allProjectIds = new Set([...plannedByProject.keys(), ...actualByProject.keys()]);
    const projectBreakdown = Array.from(allProjectIds).map(projectId => {
        const planned = plannedByProject.get(projectId) || 0;
        const actual = actualByProject.get(projectId) || 0;
        const variance = actual - planned;
        const variancePercent = planned > 0 ? (variance / planned) * 100 : 0;

        return {
            projectId,
            projectName: projectMap.get(projectId) || `Project #${projectId}`,
            plannedHours: planned,
            actualHours: actual,
            variance,
            variancePercent,
        };
    }).sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance));

    // User breakdown
    const allUserIds = new Set([...plannedByUser.keys(), ...actualByUser.keys()]);
    const userBreakdown = Array.from(allUserIds).map(userId => {
        const planned = plannedByUser.get(userId) || 0;
        const actual = actualByUser.get(userId) || 0;
        const variance = actual - planned;
        const variancePercent = planned > 0 ? (variance / planned) * 100 : 0;

        return {
            userId,
            userName: userMap.get(userId) || `User #${userId}`,
            plannedHours: planned,
            actualHours: actual,
            variance,
            variancePercent,
        };
    }).sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance));

    return {
        weekStartDate,
        weekEndDate,
        totalPlannedHours,
        totalActualHours,
        variance,
        variancePercent,
        projectBreakdown,
        userBreakdown,
    };
};

export const exportTimeEntriesToCSV = (
    timeEntries: TimeEntry[],
    projects: Project[],
    users: User[],
    tasks: Task[]
): string => {
    const projectMap = new Map(projects.map(p => [p.id, p.name]));
    const userMap = new Map(users.map(u => [u.id, u.full_name || u.username]));
    const taskMap = new Map(tasks.map(t => [t.id, t.title]));

    const headers = ['Date', 'User', 'Project', 'Task', 'Hours', 'Billable', 'Description'];
    const rows = timeEntries.map(entry => [
        entry.date,
        userMap.get(entry.user_id) || `User #${entry.user_id}`,
        entry.project_id ? (projectMap.get(entry.project_id) || `Project #${entry.project_id}`) : 'N/A',
        entry.task_id ? (taskMap.get(entry.task_id) || `Task #${entry.task_id}`) : 'N/A',
        entry.hours.toString(),
        entry.billable ? 'Yes' : 'No',
        entry.description || '',
    ]);

    const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    return csvContent;
};

export const getVarianceColor = (variancePercent: number): string => {
    if (Math.abs(variancePercent) < 10) return 'var(--success)';
    if (Math.abs(variancePercent) < 25) return 'var(--warning)';
    return 'var(--error)';
};
